import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSelector } from 'react-redux';
import { get, size, uniqBy } from 'global/lib/lodash';

export const useVendors = (realmId, opts = {}) => {
  opts = {
    include: 'realms,client,vendor,editions,scorecards',
    includeBuilders: false,
    ...opts,
  };

  // Company filter needs realms so the API can scope them to the user
  const filterByAccess = opts.filterByUserRealms ?? !!opts.includeUsersRealm;
  const include = filterByAccess && !/\brealms\b/.test(opts.include) ? `realms,${opts.include}` : opts.include;

  const meRealm = useSelector((state) => get(state, 'auth.user.realm'));
  const realm_id = realmId || get(meRealm, 'id');

  const builders = useRetrieveData(
    { url: '/realms' },
    {
      build: ['realms'],
      useCache: { reload: 20 },
      waitFor: opts.includeBuilders ? undefined : null,
      params: {
        directRelate: 1,
        filter: { tier: 'builder' },
      },
    }
  );

  const resp = useRetrieveData(
    { url: `/client-vendors` },
    {
      build: ['clientVendors'],
      useCache: { reload: 20 },
      ...opts,
      params: { ...(opts.params || {}), include },
    }
  );

  const data = (() => {
    const cvs = resp.data || [];

    // API scopes `realms` to the current user — empty means no accessible shared locations
    const visibleCvs = filterByAccess ? cvs.filter((v) => size(v.realms) > 0) : cvs;

    let rs = [
      opts.includeUsersRealm ? { id: -1, vendorId: get(meRealm, 'id'), clientId: -1, vendor: meRealm } : null,
      ...visibleCvs.filter((v) => `${v.vendorId}` !== `${realm_id}`),
    ];

    if (opts.includeBuilders) {
      const visibleCompanyIds = new Set(visibleCvs.flatMap((cv) => [`${cv.vendorId}`, `${cv.clientId}`]));
      rs = [
        ...rs,
        ...builders.data
          .filter((b) => !filterByAccess || visibleCompanyIds.has(`${b.id}`))
          .map((b) => ({
            ...b,
            id: b.id,
            vendorId: `${get(b, 'id')}`,
            vendor: b,
          })),
      ].map((v) => ({ ...v, vendorId: `${v.vendorId}` }));
    }

    return uniqBy(rs, 'vendorId').filter(Boolean);
  })();

  return {
    ...resp,
    data,
  };
};

export const useClients = (realmId, user_id, opts = {}) => {
  opts = {
    include: 'realms,client,vendor,editions,scorecards',
    ...opts,
  };

  const meRealmId = useSelector((state) => get(state, 'auth.user.realm.id'));
  const realm_id = realmId || meRealmId;
  return useRetrieveData(
    { url: `/client-vendors` },
    {
      useEffect: true,
      build: ['clientVendors'],
      useCache: { reload: 20 },
      params: {
        include: opts.include,
        filter: { vendor_id: realm_id, user_id },
      },
      ...opts,
    }
  );
};
